// The crash filters of the Safety Overview, shown once crash data is loaded. Same
// meanings as the Safety Dashboard's (safetyFilters.filterCrashes); the direction and
// the analyses come from the page's own filters above.
import React from 'react';
import { MultiPicker } from '../Safety/CorridorPicker';
import { distinctValues } from '../Safety/safetyFilters';
import { CMF_POLICIES } from '../Safety/cmfBenefit';

// the default Yaron set (2026-09-27): fatal and injury crashes
export const DEFAULT_CRASH_FILTERS = {
  years: [], severities: ['fatal', 'injury'], manners: [], lights: [], surfaces: [],
  correlatedOnly: false, excludeAnimal: false, wetOnly: false, glareOnly: false, embankmentOnly: false, passingOnly: false,
  aboveChanceOnly: false,
  // countermeasures (FHWA CMF Clearinghouse)
  showCmf: true, cmfPolicy: '45', discountRate: 7,
};

const SEVERITY_OPTIONS = [
  { key: 'fatal', label: 'Fatal' }, { key: 'injury', label: 'Injury' },
  { key: 'pdo', label: 'Property damage only' }, { key: 'unknown', label: 'Unknown' },
];
const TOGGLES = [
  ['correlatedOnly', 'Correlated only', 'Only crashes with a deficiency finding at their location, and the sites near them'],
  ['excludeAnimal', 'No animal strikes', 'Leave out animal strikes'],
  ['wetOnly', 'Wet / snow only', 'FSA mechanism: crashes on a wet, icy or snowy surface'],
  ['glareOnly', 'Glare-matched only', 'GLR mechanism: crash time inside a sun-glare window'],
  ['embankmentOnly', 'Embankment / overturn', 'GRW mechanism: embankment or overturn crashes'],
  ['passingOnly', 'Head-on / opposite', 'PSD mechanism: head-on and opposite-direction crashes'],
];

/** "All" in a picker is stored as [] (no filter). */
const picked = (value, options) => (value.length ? value : options.map((o) => o.key));
const store = (next, options) => (next.length === options.length ? [] : next);

export default function CrashFilters({ crashes, datasetYears, value, onChange, disabledCount, onRules, hasVerdicts = false }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const years = Array.from(new Set([...(datasetYears || []).map(String), ...distinctValues(crashes, 'year')])).sort();
  const opts = (key) => distinctValues(crashes, key).map((v) => ({ key: v, label: v }));
  const yearOpts = years.map((y) => ({ key: y, label: `${y} (${crashes.filter((c) => String(c.year) === y).length})` }));
  const manners = opts('manner');
  const lights = opts('light');
  const surfaces = opts('surface');
  const isDefault = JSON.stringify(value) === JSON.stringify(DEFAULT_CRASH_FILTERS);
  return (
    <div className="safety-filterbar so-crash-filters" aria-label="Crash filters">
      <span className="so-crash-label">Crashes</span>
      <MultiPicker tall label="Crash severity" noun="severities" options={SEVERITY_OPTIONS}
        selected={picked(value.severities, SEVERITY_OPTIONS)} onChange={(next) => set({ severities: store(next, SEVERITY_OPTIONS) })} />
      {yearOpts.length > 0 && (
        <MultiPicker tall label="Years" noun="years" options={yearOpts}
          selected={picked(value.years, yearOpts)} onChange={(next) => set({ years: store(next, yearOpts) })} />
      )}
      {manners.length > 0 && (
        <MultiPicker tall label="Manner" noun="manners" options={manners}
          selected={picked(value.manners, manners)} onChange={(next) => set({ manners: store(next, manners) })} />
      )}
      {lights.length > 0 && (
        <MultiPicker tall label="Light" noun="light conditions" options={lights}
          selected={picked(value.lights, lights)} onChange={(next) => set({ lights: store(next, lights) })} />
      )}
      {surfaces.length > 0 && (
        <MultiPicker tall label="Surface" noun="surfaces" options={surfaces}
          selected={picked(value.surfaces, surfaces)} onChange={(next) => set({ surfaces: store(next, surfaces) })} />
      )}
      <div className="so-toggles">
        {TOGGLES.map(([k, label, title]) => (
          <button key={k} type="button" className={`safety-toggle ${value[k] ? 'on' : ''}`} aria-pressed={Boolean(value[k])}
            title={title} onClick={() => set({ [k]: !value[k] })}>{label}</button>
        ))}
      </div>
      <button type="button" className={`safety-toggle ${value.aboveChanceOnly ? 'on' : ''}`} aria-pressed={Boolean(value.aboveChanceOnly)}
        disabled={!hasVerdicts} title={hasVerdicts ? 'Only the analyses whose statistical verdict is "Above chance" on their road'
          : 'Waits for the statistical support of the roads shown'}
        onClick={() => set({ aboveChanceOnly: !value.aboveChanceOnly })}>Above-chance verdicts only</button>
      <span className="so-cmf">
        <button type="button" className={`safety-toggle ${value.showCmf ? 'on' : ''}`} aria-pressed={Boolean(value.showCmf)}
          title="Countermeasure per deficiency site, expected benefit and the program" onClick={() => set({ showCmf: !value.showCmf })}>
          Countermeasures and benefit
        </button>
        {value.showCmf && (
          <>
            <label className="ov-inline" htmlFor="so-cmf-policy">CMF quality
              <select id="so-cmf-policy" className="ov-select" value={value.cmfPolicy} onChange={(e) => set({ cmfPolicy: e.target.value })}>
                {CMF_POLICIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </label>
            <label className="ov-inline" htmlFor="so-cmf-rate">Discount %
              <input id="so-cmf-rate" className="ov-select so-rate" type="number" min={0} max={20} step={0.5} value={value.discountRate}
                onChange={(e) => set({ discountRate: Math.max(0, Number(e.target.value) || 0) })} />
            </label>
          </>
        )}
      </span>
      {onRules && (
        <button type="button" className={`safety-toggle safety-settings-btn ${disabledCount ? 'on' : ''}`} onClick={onRules}
          title="How each analysis is correlated to a crash; switch rules on or off">
          Correlation rules{disabledCount ? ` · ${disabledCount} off` : ''}
        </button>
      )}
      {!isDefault && (
        <button type="button" className="safety-btn safety-btn-ghost" onClick={() => onChange({ ...DEFAULT_CRASH_FILTERS })}>Reset crash filters</button>
      )}
    </div>
  );
}
